import {
  Facebook,
  Instagram,
  LinkedIn,
  WhatsApp,
  YouTube,
  Telegram,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  SvgIcon,
  Snackbar,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "lib/api/api";

const TikTokIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M12.858 0h3.288a7.21 7.21 0 0 0 1.968 4.319 7.17 7.17 0 0 0 4.384 1.987v3.356a10.55 10.55 0 0 1-5.222-1.428v6.492c0 4.004-3.25 7.274-7.26 7.274a7.265 7.265 0 0 1-3.942-13.406 7.234 7.234 0 0 1 3.943-1.138c.335 0 .666.023.99.07V9.65a3.918 3.918 0 0 0-.991-.123 3.888 3.888 0 0 0-2.733 6.679 3.89 3.89 0 0 0 2.734 1.136 3.89 3.89 0 0 0 2.733-1.136 3.88 3.88 0 0 0 1.137-2.748V0Z" />
  </SvgIcon> 
);

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.dir() === "rtl";
  const language = i18n.language?.split("-")[0];
  const [socialLinks, setSocialLinks] = useState({});
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const privacyLabel =
    language === "de"
      ? "Datenschutz"
      : language === "ar"
      ? "سياسة الخصوصية"
      : "Privacy Policy";
  const cookieSettingsLabel =
    language === "de"
      ? "Cookie-Einstellungen"
      : language === "ar"
      ? "إعدادات ملفات الارتباط"
      : "Cookie Settings";
  const imprintLabel =
    language === "de"
      ? "Impressum"
      : language === "ar"
      ? "البيانات القانونية"
      : "Imprint";

  const openCookieSettings = () => {
    window.dispatchEvent(new Event("openCookieSettings"));
  };

  useEffect(() => {
    let active = true;
    api.get("/social-settings")
      .then((response) => { if (active) setSocialLinks(response.data || {}); })
      .catch(() => { if (active) setSocialLinks({}); });
    return () => { active = false; };
  }, []);

  const openSocial = (platform) => {
    const link = socialLinks[platform]?.trim();
    if (!link) {
      setComingSoonOpen(true);
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 20 },
        backgroundColor: "#fff",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            bgcolor: "#F4F7F7",
            borderRadius: 3,
            px: { xs: 2, md: 6 },
            py: { xs: 3, md: 6 },
          }}
        >
          <Grid
            container
            spacing={{ xs: 3, md: 6 }}
            justifyContent="space-between"
            alignItems="center"
            sx={{ direction: "ltr" }}
          >
            {/* Contact + Socials */}
            <Grid item xs={12} md={4}>
              <Typography
                fontSize={{ xs: "1rem", md: "16px" }}
                fontWeight="bold"
                sx={{
                  mb: 2,
                  textAlign: "center",
                  direction: isArabic ? "rtl" : "ltr",
                }}
              >
                {t("footer.contactUs")}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: { xs: "center", md: "center" },
                  gap: 1.2,
                  flexWrap: "wrap",
                  minHeight: { md: 50 },
                  alignItems: "center",
                }}
              >
                <IconButton
                  aria-label="Facebook"
                  onClick={() => openSocial("facebook")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <Facebook sx={{ color: "#1877F2" }} />
                </IconButton>
                <IconButton
                  aria-label="Instagram"
                  onClick={() => openSocial("instagram")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <Instagram sx={{ color: "#E4405F" }} />
                </IconButton>
                <IconButton
                  aria-label="WhatsApp"
                  onClick={() => openSocial("whatsapp")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <WhatsApp sx={{ color: "#25D366" }} />
                </IconButton>
                <IconButton
                  aria-label="LinkedIn"
                  onClick={() => openSocial("linkedin")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <LinkedIn sx={{ color: "#0A66C2" }} />
                </IconButton>
                <IconButton
                  aria-label="YouTube"
                  onClick={() => openSocial("youtube")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <YouTube sx={{ color: "#FF0000" }} />
                </IconButton>
                <IconButton
                  aria-label="TikTok"
                  onClick={() => openSocial("tiktok")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <TikTokIcon sx={{ color: "#000" }} />
                </IconButton>
                <IconButton
                  aria-label="Telegram"
                  onClick={() => openSocial("telegram")}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #E5E9EB",
                    width: { xs: 40, sm: 44, md: 50 },
                    height: { xs: 40, sm: 44, md: 50 },
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <Telegram sx={{ color: "#229ED9" }} />
                </IconButton>
              </Box>
            </Grid>

            {/* Logo */}
            <Grid item xs={12} md={4}>
              <Stack alignItems="center">
                <Box
                  component="img"
                  src="/al-germanist-logo.png"
                  alt="ALGermanist logo"
                  onError={(event) => {
                    event.currentTarget.src = "/al-germanist-logo.svg";
                  }}
                  sx={{
                    display: "block",
                    height: { xs: 104, sm: 124, md: 152 },
                    width: "auto",
                    maxWidth: { xs: 280, sm: 340, md: 420 },
                    objectFit: "contain",
                  }}
                />
              </Stack>
            </Grid>

            {/* Homepage tagline + legal links */}
            <Grid item xs={12} md={4}>
              <Typography
                fontSize={{ xs: "0.95rem", md: "18px" }}
                fontWeight={800}
                sx={{
                  maxWidth: 360,
                  mx: { xs: "auto", md: 0 },
                  lineHeight: { xs: 1.45, md: 1.5 },
                  color: "#1D3F38",
                  textAlign: { xs: "center", md: "center" },
                  direction: isArabic ? "rtl" : "ltr",
                  whiteSpace: "pre-line",
                }}
              >
                {t("homeBanner.tagline")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  mt: 2,
                  direction: isArabic ? "rtl" : "ltr",
                }}
              >
                <Button
                  component={Link}
                  to="/privacy-policy"
                  size="small"
                  sx={{
                    color: "#344054",
                    fontWeight: 700,
                    textTransform: "none",
                    textDecoration: "underline",
                  }}
                >
                  {privacyLabel}
                </Button>
                <Button
                  component={Link}
                  to="/imprint"
                  size="small"
                  sx={{
                    color: "#344054",
                    fontWeight: 700,
                    textTransform: "none",
                    textDecoration: "underline",
                  }}
                >
                  {imprintLabel}
                </Button>
                <Button
                  type="button"
                  onClick={openCookieSettings}
                  size="small"
                  sx={{
                    color: "#344054",
                    fontWeight: 700,
                    textTransform: "none",
                    textDecoration: "underline",
                  }}
                >
                  {cookieSettingsLabel}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <Snackbar
        open={comingSoonOpen}
        autoHideDuration={3000}
        onClose={() => setComingSoonOpen(false)}
        message={t("comingSoon")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default Footer;
